import React from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { countryCodeToCountry } from "lib/data/optionItems";
import { OpportunityDetails } from "lib/hooks/api/opportunities/useOpportunity";
import { useSavedSuppliers } from "lib/hooks/api/signals/useSavedSuppliers";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import { sysBackgroundAlternative, sysTextSecondary } from "../../../lib/themes/colors";
import { OpportunityEntityType } from "../../../lib/types/graphQLEnums";
import { opportunitySavedCardContainer, opportunitySavedCardIcon } from "./shared/styles";
import EntityCardContent from "./EntityCardContent";
import FindEntity from "./FindEntity";
import PaidTag from "./PaidTag";
import Tag from "./Tag";
import { Logo } from "./utils";

type Props = {
  suppliers: OpportunityDetails["suppliers"];
  opportunityId: string;
};

export function OpportunitySuppliers({ suppliers, opportunityId }: Props) {
  const handleViewSupplier = (id: string) => () => {
    window.location.href = `/suppliers/${id}`;
  };

  return (
    <Flex column gap={16}>
      {suppliers.map((supplier) => (
        <SupplierCard
          key={supplier.id}
          supplier={supplier}
          onView={handleViewSupplier(supplier.id)}
          opportunityId={opportunityId}
        />
      ))}
      <FindEntity entity="supplier" url={routes.suppliers} isEmptyState={!suppliers.length} />
    </Flex>
  );
}

function SupplierCard({
  supplier,
  onView,
  opportunityId,
}: {
  supplier: OpportunityDetails["suppliers"][0];
  onView: () => void;
  opportunityId: string;
}) {
  const subscription = useSubscription();
  const hasAccess = subscription.hasDataTypes("SUPPLIERS");

  const { openPaywallModal } = usePaywall();

  const country = countryCodeToCountry(supplier.countryCode ?? "");

  const { data: savedSuppliers } = useSavedSuppliers();

  const { partnerGuids, competitorGuids } = savedSuppliers || {
    partnerGuids: [],
    competitorGuids: [],
  };
  const isPartner = partnerGuids.some((guid) => guid === supplier.id);
  const isCompetitor = competitorGuids.some((guid) => guid === supplier.id);

  const totalAwards = supplier.noticeCount ?? 0;

  return (
    <Container
      onClick={() => {
        if (!hasAccess) {
          openPaywallModal("SUPPLIERS");
          return;
        }
        onView();
      }}
    >
      <EntityCardContent
        entity="supplier"
        entityId={supplier.id}
        entityType={OpportunityEntityType.Organisation}
        entityName={supplier.name}
        opportunityId={opportunityId}
      >
        <ContentWrapper column gap={24}>
          <Flex gap={12} alignItems="center">
            <Icon>
              <Logo url={supplier.logoUrl} />
            </Icon>
            <Flex gap={4} alignItems="baseline">
              <Text fontWeight="600">{supplier.name || "Untitled Supplier"}</Text>
              {country && (
                <Text description color={sysTextSecondary}>
                  {country}
                </Text>
              )}
            </Flex>
          </Flex>
          <Flex gap={8} flexWrap="wrap">
            {isPartner && (
              <Tag title="Saved to my Partners" icon="bookmark" iconProps={{ fill: true }} />
            )}
            {isCompetitor && (
              <Tag title="Saved to my Competitors" icon="bookmark" iconProps={{ fill: true }} />
            )}
            {totalAwards > 0 && (
              <Tag
                title={`${supplier.noticeCount} Direct awards`}
                backgroundColor={sysBackgroundAlternative}
                fontColor={sysTextSecondary}
              />
            )}
            {!hasAccess && <PaidTag />}
          </Flex>
        </ContentWrapper>
      </EntityCardContent>
    </Container>
  );
}

const Container = styled.div(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
}));

const ContentWrapper = styled(Flex)({ minWidth: 0 });

const Icon = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardIcon(theme),
}));
